import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const MODES = ["cash", "upi", "card", "insurance", "bank_transfer", "online", "cheque", "other"];
const EMPTY = () => ({ expense_date: today(), spent_on: "", category: "", amount: "", payment_mode: "cash", notes: "" });

export default function LabExpenses() {
  const { can } = useAuth();
  const manage = can("lab.billing.manage");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ from_date: "", to_date: "", q: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/lab/business/expenses", { params: { from_date: f.from_date || undefined, to_date: f.to_date || undefined, q: f.q || undefined } }); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY()); setFormError(""); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setForm({ ...EMPTY(), ...x, amount: String(x.amount) }); setFormError(""); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.spent_on.trim()) { setFormError("Spent-on / description is required."); return; }
    if (!(Number(form.amount) > 0)) { setFormError("Amount must be greater than zero."); return; }
    setBusy(true); setFormError("");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) await api.patch(`/lab/business/expenses/${editing.expense_id}`, payload);
      else await api.post("/lab/business/expenses", payload);
      toast.success(editing ? "Expense updated." : "Expense added."); setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  if (!can("lab.reports.view")) return <NoAccess what="lab expenses" />;

  return (
    <div data-testid="lab-expenses-page">
      <PageHeader overline="Business" title="Expenses" subtitle="Record and track laboratory operating expenses. Amounts feed Daily Business & analytics."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-expense-btn"><Plus className="h-4 w-4" /> Add expense</button>} />
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <input type="date" className={inputCls} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="exp-from" />
        <input type="date" className={inputCls} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="exp-to" />
        <input className={inputCls} placeholder="Search description" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="exp-q" />
        <div className="flex gap-2"><button className={ghostBtnCls} data-testid="exp-search">Search</button><button type="button" className={ghostBtnCls} onClick={() => window.print()}>Print</button></div>
      </form>
      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <>
          <div className="panel px-4 py-2 mb-4 inline-block text-sm">Total expenses: <span className="font-semibold" data-testid="exp-total">₹{Number(data.total).toLocaleString("en-IN")}</span></div>
          <Table testid="lab-expenses-table" columns={["Date", "Spent on", "Category", "Amount", "Mode", "Added by", manage ? "" : ""]}
            rows={data.items} empty={<EmptyState title="No expenses" subtitle="Record your first expense." testid="lab-expenses-empty" />}
            renderRow={(x) => (
              <tr key={x.expense_id} data-testid={`lab-expense-row-${x.expense_id}`}>
                <Td className="text-slate-500 text-xs">{x.expense_date}</Td>
                <Td className="font-medium text-slate-900">{x.spent_on}<span className="block text-xs text-slate-400">{x.notes}</span></Td>
                <Td className="text-slate-600">{x.category || "—"}</Td>
                <Td className="text-slate-700">₹{Number(x.amount).toLocaleString("en-IN")}</Td>
                <Td className="text-slate-600 capitalize">{(x.payment_mode || "").replace("_", " ")}</Td>
                <Td className="text-slate-500 text-xs">{x.added_by}</Td>
                {manage && <Td><button onClick={() => openEdit(x)} className={ghostBtnCls} data-testid={`edit-expense-${x.expense_id}`}>Edit</button></Td>}
              </tr>
            )} />
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit expense" : "Add expense"} testid="lab-expense-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date *"><input type="date" className={inputCls} data-testid="expense-date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></Field>
            <Field label="Amount (₹) *"><input type="number" min="1" className={inputCls} data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Spent on *"><input className={inputCls} data-testid="expense-spenton" value={form.spent_on} onChange={(e) => setForm({ ...form, spent_on: e.target.value })} /></Field>
            <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Consumables, Rent, Salary…" /></Field>
            <Field label="Payment mode"><select className={inputCls} value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}>{MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select></Field>
            <Field label="Notes"><input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          {formError && <p className="text-sm text-red-600" data-testid="expense-form-error">{formError}</p>}
          <div className="flex justify-end gap-3"><button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button><button disabled={busy} className={primaryBtnCls} data-testid="save-expense-btn">{busy ? "Saving…" : "Save"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
